import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { DeskSession } from './useDeskSession'
import { hhmm } from '../../lib/useFictionalClock'
import Instruments from './parts/Instruments'
import InstrumentStrip, { type Readout } from './parts/InstrumentStrip'
import Traffic from './parts/Traffic'
import Outbox from './parts/Outbox'
import OpeningRun from '../../components/OpeningRun'
import Dossier, { cardsAsGiven } from './parts/Dossier'
import Boot from './parts/Boot'
import styles from './Surface.module.css'
import { useT } from '../../lib/i18n'

/**
 * The desk, for a device with room and a keyboard.
 *
 * Purely what the surface SHOWS — every fact and every act arrives already
 * decided in `session`. A phone surface, when one is written, is a sibling of
 * this file over the same hook, not a set of breakpoints inside it.
 *
 * You are not there. You are at the other end of a wire, in a room with a clock
 * and a notebook, and everything you know arrives as traffic — so this is an
 * instrument panel and a transcript, never a scene.
 */
export default function DeskDesktop({ session }: { session: DeskSession }) {
  const t = useT()
  const s = session
  // Declared before every early return below — the surface has several, and a
  // hook after one of them is a hook that sometimes does not run.
  const [dossierOpen, setDossierOpen] = useState(false)
  const [booted, setBooted] = useState(false)

  /*
   * ARRIVED THROUGH A DOOR — the choice is already made, so there is nothing to
   * ask. No threshold: just the opening, and the error if it does not open.
   *
   * The threshold screen used to carry the title, role and blurb behind a TAKE
   * THE DESK button, which was the pack page again one click later. That page
   * now exists for real (DoorChooser), so this would be a second one.
   */
  if (!s.sessionId && s.autoBegin) {
    return (
      <div className={styles.threshold}>
        <div className={styles.thresholdInner}>
          {s.error ? (
            <>
              <p className={styles.error}>{s.error}</p>
              <button className={styles.enter} onClick={s.begin} disabled={s.starting}>
                {s.starting ? 'OPENING…' : 'TRY AGAIN'}
              </button>
            </>
          ) : (
            <p className={styles.opening}>{t('desk.opening')}</p>
          )}
          <div>
            <Link to={s.scenario ? `/p/${s.scenario.id}` : '/'} className={styles.back}>
              ‹ BACK
            </Link>
          </div>
        </div>
      </div>
    )
  }

  /*
   * The threshold, kept for packs with NO doors — they have no pack page in
   * front of them, so this is the only place their title, role and description
   * are shown before committing.
   */
  if (!s.sessionId) {
    return (
      <div className={styles.threshold}>
        <div className={styles.thresholdInner}>
          <h1 className={styles.title}>{s.station}</h1>
          {s.role && <p className={styles.role}>{s.role}</p>}
          {s.scenario && <p className={styles.opening}>{s.scenario.blurb}</p>}
          <button className={styles.enter} onClick={s.begin} disabled={s.starting}>
            {s.starting ? 'OPENING…' : 'TAKE THE DESK'}
          </button>
          {s.error && <p className={styles.error}>{s.error}</p>}
          <div>
            <Link to="/" className={styles.back}>
              ‹ BACK
            </Link>
          </div>
        </div>
      </div>
    )
  }

  /*
   * THE OPENING RUN (brief §6.4). Between the session opening and the desk
   * mounting there is one question outstanding — does this pack have a run, and
   * has it been played? — and until it is answered neither answer may be drawn.
   * Rendering the desk first and swapping to the cards a moment later would show
   * the player the end of the story before its cover.
   */
  if (!s.openingSettled) {
    return (
      <div className={styles.threshold}>
        <div className={styles.thresholdInner}>
          <p className={styles.opening}>opening the desk…</p>
        </div>
      </div>
    )
  }

  if (s.openingRunning && s.opening) {
    return <OpeningRun opening={s.opening} onComplete={s.finishOpening} />
  }

  /*
   * POWER THE DESK (§6.1). Once per mount, after the run and before the desk —
   * the equipment coming up as you sit down. Skippable by any interaction, and
   * it ends itself regardless, so it can never become something to wait through.
   *
   * ⚠ `shape` is hardcoded to the arranged variant because the wire does not
   * carry the selector — see Boot.tsx's note. Both variants exist and are
   * exercised in the dev harness; one field on the pack card switches them.
   */
  if (!booted) {
    return <Boot shape="arranged" onDone={() => setBooted(true)} />
  }

  /*
   * The instrument strip (brief §3). Each readout appears ONLY when its data
   * exists — absence renders nothing, never a dash standing in for a reading.
   *
   * ⚠ [CONTRACT PENDING]: DEPTH · HEART · GAS · GEOPHONE · CHARGE · NODE have no
   * server-side source today, so they are simply not constructed here. They
   * arrive as readouts the moment the contract carries them; the strip itself
   * needs no change.
   */
  const minsToSked =
    s.nextCall && s.now
      ? Math.round((s.nextCall.getTime() - s.now.getTime()) / 60000)
      : null

  const readouts: (Readout | null)[] = [
    s.now ? { key: 'clock', label: 'desk', value: hhmm(s.now) } : null,
    s.nextCall
      ? {
          key: 'sked',
          label: 'next sked',
          value: hhmm(s.nextCall),
          hint:
            minsToSked === null ? undefined : minsToSked > 0 ? `in ${minsToSked}m` : 'due',
        }
      : null,
  ]

  /*
   * THE DOSSIER (brief §5). The opening cards are its seed and its only live
   * content today: given documents and received artifacts have no server surface
   * yet (see Dossier.tsx), so they arrive as empty arrays and the drawer renders
   * their absence rather than a placeholder.
   */
  const given = s.opening ? cardsAsGiven(s.opening.cards) : []

  return (
    <div className={styles.desk}>
      {given.length > 0 && (
        <button
          className={styles.paperTab}
          onClick={() => setDossierOpen(true)}
          aria-label={t('desk.dossier.open')}
        >
          paper
        </button>
      )}

      {dossierOpen && (
        <Dossier given={given} received={[]} onClose={() => setDossierOpen(false)} />
      )}

      <Instruments
        station={s.station}
        now={s.now}
        arrangement={s.arrangement}
        onArrange={s.arrange}
        nextCall={s.nextCall}
        onSetNextCall={s.setNextCall}
        plan={s.plan}
        onSetPlan={s.setPlan}
        onJump={s.jump}
        onSignOff={s.signOff}
        jumpBusy={s.jumpBusy}
        skedBusy={s.skedBusy}
        skedLabel={s.skedLabel}
        disabled={s.terminated}
      />

      <InstrumentStrip readouts={readouts} />

      {/*
        `standing` (brief §2.5 — last node relayed, stated turnaround during a
        dark window) is deliberately not passed: the server exposes neither node
        data nor a past-the-chain state, and inventing a reassuring line would be
        worse than the silence it papers over. It wires up when the contract does.
      */}
      <Traffic rows={s.rows} working={s.working} />

      {s.terminated ? (
        <div className={styles.ended}>
          — {s.terminationReason ?? 'the channel closes'} —
        </div>
      ) : (
        <Outbox onSend={s.send} />
      )}
    </div>
  )
}
